import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Box,
  SkeletonBodyText,
  Filters,
  ChoiceList,
  Badge,
  ResourceList,
  ResourceItem,
  Bleed,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useCountries } from "@shared/hooks/common/useCountries";
import { SearchableChoiceList } from "@shared/ui";

// Import Brand type from dashboard
import type { Brand } from "@features/events/types";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";

interface AssignBrandsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBrandIds: string[];
  onAssign: (brandIds: string[]) => void;
}

export default function AssignBrandsModal({
  isOpen,
  onClose,
  selectedBrandIds,
  onAssign,
}: AssignBrandsModalProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();
  const { countries } = useCountries();

  // Local state for modal
  const [localSelectedIds, setLocalSelectedIds] = useState<string[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [previouslyUsedFilter, setPreviouslyUsedFilter] =
    useState<string>("used");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Track if user has manually set the filter (to prevent auto-removal later)
  const [hasUserSetFilter, setHasUserSetFilter] = useState(false);

  const selectedIds = localSelectedIds ?? selectedBrandIds;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all brands (no server-side filtering)
  const { data: allBrands = [], isLoading } = useQuery<Brand[]>({
    queryKey: ["assign-brands", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/brands`),
    enabled: Boolean(hcc && isOpen),
    placeholderData: keepPreviousData,
  });

  const hasUsedBrands = useMemo(
    () => allBrands.some((brand: Brand) => brand.isUsedBefore === true),
    [allBrands]
  );

  const effectivePreviouslyUsedFilter =
    !hasUserSetFilter && previouslyUsedFilter === "used" && !hasUsedBrands
      ? ""
      : previouslyUsedFilter;

  // Filter brands locally
  const brands = useMemo(() => {
    let filtered = [...allBrands];

    // Apply search filter
    if (debouncedQuery) {
      const searchLower = debouncedQuery.toLowerCase();
      filtered = filtered.filter(
        (brand: Brand) =>
          brand.name?.en?.toLowerCase().includes(searchLower) ||
          brand.name?.bg?.toLowerCase().includes(searchLower) ||
          brand.headline?.en?.toLowerCase().includes(searchLower) ||
          brand.headline?.bg?.toLowerCase().includes(searchLower) ||
          brand.description?.en?.toLowerCase().includes(searchLower) ||
          brand.description?.bg?.toLowerCase().includes(searchLower)
      );
    }

    // Apply country filter
    if (selectedCountry) {
      filtered = filtered.filter(
        (brand: Brand) =>
          brand.geoAddress?.countryISO3?.toLowerCase() === selectedCountry
      );
    }

    // Apply previously used filter
    if (effectivePreviouslyUsedFilter === "used") {
      filtered = filtered.filter((brand: Brand) => brand.isUsedBefore === true);
    }

    return filtered;
  }, [allBrands, debouncedQuery, selectedCountry, effectivePreviouslyUsedFilter]);

  const resetModalState = useCallback(() => {
    setLocalSelectedIds(null);
    setSearchQuery("");
    setSelectedCountry("");
    setPreviouslyUsedFilter("used");
    setHasUserSetFilter(false);
  }, []);

  // Handle brand selection
  const handleBrandToggle = (brandId: string) => {
    setLocalSelectedIds((prev) => {
      const current = prev ?? selectedBrandIds;
      return current.includes(brandId)
        ? current.filter((id) => id !== brandId)
        : [...current, brandId];
    });
  };

  // Handle assign
  const handleAssign = () => {
    onAssign(selectedIds);
    resetModalState();
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    resetModalState();
    onClose();
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCountry("");
    setPreviouslyUsedFilter("");
    setHasUserSetFilter(true);
  };

  // Country options from API
  const countryOptions = [
    { label: t("common.allCountries"), value: "" },
    ...countries.map((country) => ({
      label: country.name,
      value: country.iso3?.toLowerCase(),
    })),
  ];

  // Filters configuration
  const filters = [
    {
      key: "country",
      label: t("common.country"),
      filter: (
        <SearchableChoiceList
          label={t("common.country")}
          options={countryOptions}
          value={selectedCountry}
          onChange={(value) => setSelectedCountry(value)}
        />
      ),
      shortcut: true,
    },
    {
      key: "previouslyUsed",
      label: t("common.previouslyUsed"),
      filter: (
        <ChoiceList
          title={t("common.previouslyUsed")}
          titleHidden
          choices={[
            {
              label: t("common.previouslyUsed"),
              value: "used",
            },
            {
              label: t("common.notPreviouslyUsed"),
              value: "notUsed",
            },
          ]}
          selected={
            effectivePreviouslyUsedFilter ? [effectivePreviouslyUsedFilter] : []
          }
          onChange={(value) => {
            setPreviouslyUsedFilter(value[0] || "");
            setHasUserSetFilter(true); // Mark as manually set by user
          }}
          allowMultiple={false}
        />
      ),
      shortcut: true,
    },
  ];

  // Applied filters
  const appliedFilters = [];
  if (selectedCountry) {
    const countryLabel =
      countryOptions.find((c) => c.value === selectedCountry)?.label ||
      selectedCountry;
    appliedFilters.push({
      key: "country",
      label: `${t("common.country")}: ${countryLabel}`,
      onRemove: () => setSelectedCountry(""),
    });
  }
  if (effectivePreviouslyUsedFilter) {
    const filterLabel =
      effectivePreviouslyUsedFilter === "used"
        ? t("common.previouslyUsed")
        : t("common.notPreviouslyUsed");
    appliedFilters.push({
      key: "previouslyUsed",
      label: filterLabel,
      onRemove: () => {
        setPreviouslyUsedFilter("");
        setHasUserSetFilter(true); // Mark as manually set by user
      },
    });
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      title={t("eventDetails.partnerBrands.modal.title")}
      size="large"
      primaryAction={{
        content: t("eventDetails.partnerBrands.modal.assignButton"),
        onAction: handleAssign,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleCancel,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Hint */}
          <Text as="p" variant="bodyMd" tone="subdued">
            {t("eventDetails.partnerBrands.modal.hint")}
          </Text>
        </BlockStack>

        <Bleed marginInline="400" marginBlock="400">
          {/* Brands list with integrated filtering */}
          {isLoading ? (
            <Box padding="400">
              <BlockStack gap="200">
                {[1, 2, 3].map((i) => (
                  <InlineStack key={i} gap="200" blockAlign="center">
                    <Box width="24px" minHeight="24px" />
                    <Box minHeight="40px">
                      <SkeletonBodyText lines={1} />
                    </Box>
                    <BlockStack gap="100">
                      <Box
                        width="200px"
                        background="bg-surface"
                        minHeight="16px"
                      />
                      <Box
                        width="150px"
                        background="bg-surface"
                        minHeight="12px"
                      />
                    </BlockStack>
                  </InlineStack>
                ))}
              </BlockStack>
            </Box>
          ) : (
            <ResourceList
              resourceName={{ singular: "brand", plural: "brands" }}
              items={brands}
              selectedItems={selectedIds}
              onSelectionChange={(selection) =>
                setLocalSelectedIds(selection as string[])
              }
              selectable
              filterControl={
                <Filters
                  queryValue={searchQuery}
                  queryPlaceholder={t(
                    "eventDetails.partnerBrands.modal.searchPlaceholder"
                  )}
                  onQueryChange={setSearchQuery}
                  onQueryClear={() => setSearchQuery("")}
                  onClearAll={handleClearFilters}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  closeOnChildOverlayClick
                />
              }
              emptyState={
                <Box padding="400">
                  <Text
                    as="p"
                    variant="bodyMd"
                    alignment="center"
                    tone="subdued"
                  >
                    {t("eventDetails.partnerBrands.modal.noBrands")}
                  </Text>
                </Box>
              }
              renderItem={(brand: Brand) => {
                const brandName = getLocalizedText(brand.name);
                const brandHeadline = getLocalizedText(brand.headline);

                return (
                  <ResourceItem
                    id={brand.id}
                    media={
                      <EventsDefaultAvatar
                        name={brand.name}
                        singleImage={brand.logoSquare}
                      />
                    }
                    accessibilityLabel={`Select ${brandName}`}
                    onClick={() => handleBrandToggle(brand.id)}
                    verticalAlignment="center"
                  >
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      wrap={false}
                    >
                      <BlockStack gap="100">
                        <InlineStack
                          gap="200"
                          align="start"
                          blockAlign="baseline"
                        >
                          <Text
                            as="h3"
                            variant="bodyMd"
                            fontWeight="semibold"
                            breakWord
                          >
                            {brandName}
                          </Text>
                          {brand.role && (
                            <Badge
                              tone={
                                brand.role === "organizer" ||
                                brand.role === "Organizer"
                                  ? "success"
                                  : undefined
                              }
                            >
                              {brand.role.charAt(0).toUpperCase() +
                                brand.role.slice(1)}
                            </Badge>
                          )}
                        </InlineStack>
                        {brandHeadline && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {brandHeadline}
                          </Text>
                        )}
                        <Text as="p" variant="bodySm" tone="subdued">
                          {getLocalizedText(brand.geoAddress?.summary)}
                        </Text>
                      </BlockStack>
                      <InlineStack align="end" gap="200">
                        {brand.isUsedBefore && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {t("common.previouslyUsed")}
                          </Text>
                        )}
                      </InlineStack>
                    </InlineStack>
                  </ResourceItem>
                );
              }}
            />
          )}
        </Bleed>
      </Modal.Section>
    </Modal>
  );
}
